import type { LineData } from "components/charting/types/Chart.types";
import { useMemo } from "react";
import useChartSelections from "../../../components/charting/hooks/useChartSelections";
import useGetManyPersonalBestQuery from "hooks/useGetManyPersonalBestQuery";
import { MetadataType } from "types/metadata";
import { SelectedChartMeasure, SelectedChartMeasureExercise } from "components/charting/types/ChartingSelections.types";

function useChartLinePersonalBest(lines: LineData[], sourceIds: string[], validMeasures: SelectedChartMeasure[]) {
  const { showPersonalBest } = useChartSelections();

  const personalBestsArgs = useMemo(() => {
    return validMeasures
      .filter(
        (measure): measure is SelectedChartMeasureExercise =>
          measure.type === MetadataType.EXERCISE && measure.metric?.field !== undefined
      )
      .flatMap((measure) =>
        sourceIds.map((athleteId) => ({
          athleteId,
          exerciseId: measure.id,
          metricField: measure.metric.field,
          type: MetadataType.EXERCISE,
          sortType: measure.metric.sortType,
        }))
      );
  }, [sourceIds, validMeasures]);

  const personalBests = useGetManyPersonalBestQuery(personalBestsArgs, showPersonalBest);

  const composedData = useMemo(() => {
    if (!personalBests.data.length) {
      return lines.map((lineData) => ({ ...lineData, common: { ...lineData.common, best: null } })) as LineData[];
    }

    return lines.map((lineData) => {
      const pb = personalBests.data?.find(
        (pb) =>
          pb.athleteId === lineData.source.id &&
          pb.exerciseId === lineData.measure.id &&
          pb.metricField === lineData.measure.metricField
      );

      if (!pb || pb.value === null) {
        return { ...lineData, common: { ...lineData.common, best: null } } as LineData;
      }

      return { ...lineData, common: { ...lineData.common, best: pb.value } } as LineData;
    });
  }, [lines, personalBests.data]);

  return { data: composedData, personalBests: personalBests.data };
}
export default useChartLinePersonalBest;
